import { useCallback, useMemo, useSyncExternalStore } from 'react'

import { orderBy } from 'lodash'

import { SupportedChainId } from '~/constants/chains'
import { IAddress } from '~/types'
import { Address } from '~/utils/address'
import { Formatted, Address as AddressType } from '~/utils/address/types'

const STORAGE_KEY = 'splits.smartAccounts.frequentlyUsedTokens'
const STORAGE_EVENT = 'frequently-used-tokens-storage-change'

export type FrequentlyUsedToken = {
  address: Formatted<AddressType>
  chainId: SupportedChainId
  uses: number
}

const readRaw = (): string => {
  if (typeof window === 'undefined') return ''
  return window.localStorage.getItem(STORAGE_KEY) ?? ''
}

const parse = (raw: string): FrequentlyUsedToken[] => {
  if (!raw) return []
  try {
    const parsed = JSON.parse(raw) as FrequentlyUsedToken[]
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

const read = (): FrequentlyUsedToken[] => parse(readRaw())

const emit = () => {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new CustomEvent(STORAGE_EVENT))
}

const write = (tokens: FrequentlyUsedToken[]) => {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(tokens))
    emit()
  } catch {
    // Ignore localStorage failures (private browsing, quota, etc.).
  }
}

const subscribe = (onStoreChange: () => void) => {
  if (typeof window === 'undefined') return () => undefined

  const handleStorage = (event: StorageEvent) => {
    if (event.key === STORAGE_KEY) onStoreChange()
  }
  const handleCustomEvent = () => onStoreChange()

  window.addEventListener('storage', handleStorage)
  window.addEventListener(STORAGE_EVENT, handleCustomEvent)

  return () => {
    window.removeEventListener('storage', handleStorage)
    window.removeEventListener(STORAGE_EVENT, handleCustomEvent)
  }
}

export const useFrequentlyUsedTokens = () => {
  const raw = useSyncExternalStore(subscribe, readRaw, () => '')
  const frequentlyUsedTokens = useMemo(() => parse(raw), [raw])

  const registerTokenUse = useCallback(
    (address: IAddress, chainId: SupportedChainId) => {
      const formatted = Address.format(address)
      const current = read()
      const idx = current.findIndex(
        (t) => Address.equal(t.address, address) && t.chainId === chainId,
      )
      const next =
        idx >= 0
          ? current.map((t, i) => (i === idx ? { ...t, uses: t.uses + 1 } : t))
          : [...current, { address: formatted, chainId, uses: 1 }]
      write(orderBy(next, ['uses'], ['desc']))
    },
    [],
  )

  const getTokenUses = useCallback(
    (address: IAddress, chainId: SupportedChainId) =>
      frequentlyUsedTokens.find(
        (t) => t.chainId === chainId && Address.equal(t.address, address),
      )?.uses ?? 0,
    [frequentlyUsedTokens],
  )

  return { registerTokenUse, getTokenUses, frequentlyUsedTokens }
}
